import { useEffect, useState } from "react";
import logger from "../utils/logger";
import axios from "axios";
import { API, getAuthHeaders } from "../context/AuthContext";
import { Envelope, PaperPlaneTilt, TestTube } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";

export default function EmailSettings() {
  const [config, setConfig] = useState({ api_key: "", sender_email: "onboarding@resend.dev", enabled: false });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [fullKey, setFullKey] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/settings/email`, { headers: getAuthHeaders() })
      .then((res) => setConfig(res.data))
      .catch((err) => logger.error("Email settings error:", err));
  }, []);

  const handleSave = async () => {
    const keyToSave = fullKey || config.api_key;
    if (!keyToSave) { toast.error("API key is required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/settings/email`, {
        api_key: keyToSave,
        sender_email: config.sender_email,
        enabled: config.enabled
      }, { headers: getAuthHeaders() });
      toast.success("Email settings saved");
      setFullKey("");
    } catch (err) {
      toast.error("Failed to save settings");
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/admin/settings/email/test`, {}, { headers: getAuthHeaders() });
      toast.success("Test email sent!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test email");
    } finally { setTesting(false); }
  };

  return (
    <div className="rounded-md border border-white/10 p-5" style={{ background: "#121212" }} data-testid="email-settings">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "rgba(167, 139, 250, 0.1)" }}>
          <Envelope size={20} weight="duotone" className="text-[#A78BFA]" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Email Notifications (Resend)
          </h3>
          <p className="text-[10px] text-[#A1A1AA] mt-0.5">Send email alerts for admin actions and 2FA codes</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between p-3 rounded-md border border-white/10 bg-white/[0.02]">
          <div>
            <p className="text-xs text-white">Enable Email Notifications</p>
            <p className="text-[10px] text-[#A1A1AA]">Send emails via Resend API</p>
          </div>
          <Switch
            data-testid="email-enabled-switch"
            checked={config.enabled}
            onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA] mb-1.5 block">Resend API Key</label>
          <Input
            data-testid="resend-api-key-input"
            type="password"
            placeholder={config.configured ? "Key saved (enter new to update)" : "Enter Resend API key (re_...)"}
            value={fullKey}
            onChange={(e) => setFullKey(e.target.value)}
            className="bg-transparent border-white/10 text-white placeholder:text-[#A1A1AA]/40 text-sm focus-visible:ring-[#A78BFA]"
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA] mb-1.5 block">Sender Email</label>
          <Input
            data-testid="sender-email-input"
            placeholder="onboarding@resend.dev"
            value={config.sender_email}
            onChange={(e) => setConfig({ ...config, sender_email: e.target.value })}
            className="bg-transparent border-white/10 text-white placeholder:text-[#A1A1AA]/40 text-sm focus-visible:ring-[#A78BFA]"
          />
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            data-testid="save-email-settings"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-bold text-black bg-[#A78BFA] hover:bg-[#8B5CF6] transition-colors disabled:opacity-50"
          >
            <PaperPlaneTilt size={14} weight="bold" />
            {saving ? "Saving..." : "Save Settings"}
          </button>
          <button
            data-testid="test-email"
            onClick={handleTest}
            disabled={testing || !config.configured}
            className="flex items-center gap-1.5 px-4 py-2 rounded-md text-xs border border-white/20 text-white hover:border-white/40 transition-colors disabled:opacity-50"
          >
            <TestTube size={14} />
            {testing ? "Sending..." : "Send Test"}
          </button>
        </div>

        <div className="p-3 rounded-md border border-white/5 bg-white/[0.01]">
          <p className="text-[10px] text-[#A1A1AA]">
            <strong className="text-white">Setup:</strong> Get your API key at <span className="text-[#A78BFA]">resend.com/api-keys</span>. In test mode, emails only deliver to verified addresses.
          </p>
        </div>
      </div>
    </div>
  );
}
